<template>
    <!-- Single-tour treatment for the related-tours rail: give the one tour the
         whole panel. Plays its video when it has one, otherwise shows a collage
         of its photos. -->
    <div ref="target">
        <component
            :is="hasAgency ? 'a' : Link"
            prefetch="hover"
            :href="url"
            @click="emit('click')"
            class="group block overflow-hidden"
        >
            <!-- Video tour: a muted, looped, chrome-less clip fit to the 16:9
                 card (full frame, no zoom crop), with a strip of photos below. -->
            <div v-if="hasVideo" class="flex flex-col gap-1">
                <div
                    class="relative rounded-xl overflow-hidden bg-neutral-900"
                    style="aspect-ratio: 16/9"
                >
                    <video-placeholder
                        class="w-full h-full object-cover"
                        :poster="poster"
                        :vimeo_id="tour.videos[0].vimeoId ?? undefined"
                        :youtube_id="tour.videos[0].youtubeId ?? undefined"
                    />
                    <promotions :tour="tour" />
                </div>

                <div v-if="images.length" class="flex gap-1">
                    <div
                        v-for="(img, i) in images.slice(0, 3)"
                        :key="i"
                        class="relative flex-1 overflow-hidden rounded-md"
                        style="aspect-ratio: 4/3"
                    >
                        <img
                            :src="img"
                            :alt="tour.name"
                            class="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                </div>
            </div>

            <!-- Photo tour: a hero tile beside up to two supporting shots, or a
                 single cover image when there's only one photo. -->
            <div
                v-else
                class="relative rounded-xl overflow-hidden bg-neutral-100"
                style="aspect-ratio: 16/10"
            >
                <div
                    v-if="images.length > 1"
                    class="absolute inset-0 grid grid-cols-3 grid-rows-2 gap-0.5"
                >
                    <div class="col-span-2 row-span-2 overflow-hidden">
                        <img
                            :src="heroImage"
                            :alt="tour.name"
                            class="w-full h-full object-cover transition duration-500 group-hover:scale-105"
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                    <div
                        v-for="(img, i) in sideImages"
                        :key="i"
                        class="overflow-hidden"
                        :class="sideImages.length < 2 ? 'row-span-2' : ''"
                    >
                        <img
                            :src="img"
                            :alt="tour.name"
                            class="w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                </div>

                <img
                    v-else
                    :src="poster"
                    :alt="tour.name"
                    class="absolute inset-0 w-full h-full object-cover transition duration-500 group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                />

                <promotions :tour="tour" />
            </div>

            <div class="py-4 flex flex-col gap-2">
                <div>
                    <h5
                        class="text-base !font-semibold leading-snug body-text m-0 line-clamp-2"
                    >
                        {{ tour.name }}
                    </h5>
                    <rating
                        v-if="tour.average_rating"
                        :rating="tour.average_rating"
                        class="mt-1"
                    />
                </div>

                <p
                    v-if="tour.short_description"
                    class="text-sm text-neutral-600 m-0 line-clamp-3"
                >
                    {{ tour.short_description }}
                </p>

                <div class="pt-1">
                    <price :tour="tour" size="grid" />
                </div>
            </div>
        </component>
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { Tour, VideoT } from "@/types";
import { Link } from "@inertiajs/vue3";
import Promotions from "./Promotions.vue";
import Price from "./Price.vue";
import Rating from "@/Components/Reviews/Rating.vue";
import useAgency from "@/Hook/useAgency";
import { useImpressionObserver } from "@/Hook/useImpressionObserver";
import VideoPlaceholder from "@/Components/VideoPlaceholder.vue";

const emit = defineEmits(["click", "impression"]);

const { hasAgency } = useAgency();

const props = defineProps<{
    tour: Tour;
    url?: string;
}>();

// First playable video, if any.
const video = computed<VideoT | undefined>(() =>
    props.tour.videos?.find((v) => v.url || v.youtubeId || v.vimeoId),
);

// Only treat a video `url` as a native source; hosted ids go through the embed.
const nativeSrc = computed<string | undefined>(() =>
    video.value && !video.value.youtubeId && !video.value.vimeoId
        ? video.value.url
        : undefined,
);

// Chrome-less, auto-playing embed URL fit to the card. YouTube needs
// `playlist=<id>` for the loop; Vimeo's `background=1` is already clean.
const embedUrl = computed<string | undefined>(() => {
    const v = video.value;
    if (!v) return undefined;

    if (v.youtubeId) {
        const params = new URLSearchParams({
            autoplay: "1",
            mute: "1",
            loop: "1",
            playlist: v.youtubeId,
            controls: "0",
            modestbranding: "1",
            rel: "0",
            playsinline: "1",
            disablekb: "1",
            fs: "0",
            iv_load_policy: "3",
        });
        return `https://www.youtube.com/embed/${v.youtubeId}?${params}`;
    }

    if (v.vimeoId) {
        const params = new URLSearchParams({
            autoplay: "1",
            muted: "1",
            loop: "1",
            background: "1",
            playsinline: "1",
        });
        return `https://player.vimeo.com/video/${v.vimeoId}?${params}`;
    }

    return undefined;
});

const hasVideo = computed<boolean>(() => !!(embedUrl.value || nativeSrc.value));

const poster = computed<string>(() => {
    const cover =
        props.tour.images?.[props.tour.cover_photo] ?? props.tour.images?.[0];
    return cover?.large ?? cover?.medium ?? "";
});

// Distinct photos (largest first) available for the mosaic tiles.
const images = computed<string[]>(() =>
    (props.tour.images ?? [])
        .map((img) => img.large ?? img.medium)
        .filter((src): src is string => !!src),
);

// Photo-collage hero (first photo) and its up-to-two supporting side tiles.
const heroImage = computed<string>(() => images.value[0] ?? poster.value);
const sideImages = computed<string[]>(() => images.value.slice(1, 3));

const { target } = useImpressionObserver((entry) => emit("impression", entry));

// Keep the poster over a video for a beat after mount so the player's start-up
// chrome never shows, then fade to the running clip. Only relevant for videos;
// photo tours reveal immediately.
const posterCover = ref(true);
let posterTimer: ReturnType<typeof setTimeout> | undefined;

onMounted(() => {
    if (hasVideo.value) {
        posterTimer = setTimeout(() => (posterCover.value = false), 2200);
    } else {
        posterCover.value = false;
    }
});

onBeforeUnmount(() => clearTimeout(posterTimer));
</script>

<style scoped>
.rtf-poster-fade-leave-active {
    transition: opacity 0.6s ease;
}

.rtf-poster-fade-leave-to {
    opacity: 0;
}
</style>
